import WeaponType from "../../enums/WeaponType";
import Game from "../../game/Game";
import { DEFAULT_SCALE } from "../../consts/UI";
import type { UpgradesInfo } from "../../../handlers/types";

export const DEFAULT_CHARACTER_ID = "default-guy";
export const DEFAULT_CHARACTER_NAME = "default-guy";
export const DEFAULT_CHARACTER_DESCRIPTION = "";
export const DEFAULT_CHARACTER_STARTING_WEAPON = WeaponType.HASHBLADE;

interface CharacterCreationOptions {
  id?: string;
  name?: string;
  description?: string;
  lookId?: string | null;
  startingWeapon?: WeaponType;
}

export interface CharacterStats {
  [key: string]: number;
}

export interface CharacterVisuals {
  spriteName: string;
  animationKey?: string;
  scale: number;
}

export interface CharacterData {
  id: string;
  name: string;
  description: string;
  stats: CharacterStats;
  startingWeapon: string;
  lookId?: string;
  upgradeLevels: {
    [key: string]: number;
  };
}

export class Character {
  private data: CharacterData;

  constructor(data: any) {
    // Map snake_case to camelCase
    const mappedData: CharacterData = {
      id: data.id,
      name: data.name,
      description: data.description,
      stats: data.stats,
      startingWeapon: data.starting_weapon || data.startingWeapon,
      lookId: data.look_id || data.lookId,
      upgradeLevels: data.upgrade_levels || data.upgradeLevels || {},
    };

    this.data = {
      ...mappedData,
      startingWeapon: mappedData.startingWeapon,
    };
  }

  static createInitialCharacter(
    upgradesInfo: UpgradesInfo,
    options: CharacterCreationOptions = {}
  ): Character {
    const stats: CharacterStats = {};

    const defaults = upgradesInfo.defaults ?? {};
    Object.values(defaults).forEach((entry) => {
      if (!entry) return;
      stats[entry.characteristic] = entry.baseValue ?? 0;
    });

    const currentStats = upgradesInfo.currentStats ?? {};
    Object.entries(currentStats).forEach(([key, value]) => {
      if (typeof value === "number") {
        stats[key] = value;
      }
    });

    const upgradeLevels: Record<string, number> = {};
    const currentLevels = upgradesInfo.currentLevels ?? {};
    Object.entries(currentLevels).forEach(([key, value]) => {
      if (typeof value === "number") {
        upgradeLevels[key] = value;
      }
    });

    const id = options.id ?? DEFAULT_CHARACTER_ID;
    const name = options.name ?? DEFAULT_CHARACTER_NAME;
    const description = options.description ?? DEFAULT_CHARACTER_DESCRIPTION;
    const lookId = options.lookId ?? undefined;
    const startingWeapon =
      options.startingWeapon ?? DEFAULT_CHARACTER_STARTING_WEAPON;

    return new Character({
      id,
      name,
      description,
      stats,
      startingWeapon,
      lookId,
      upgradeLevels,
    });
  }

  get id(): string {
    return this.data.id;
  }

  get name(): string {
    return this.data.name;
  }

  get description(): string {
    return this.data.description;
  }

  get stats(): CharacterStats {
    return { ...this.data.stats };
  }

  get startingWeapon(): WeaponType {
    return this.data.startingWeapon as WeaponType;
  }

  get lookId(): string | undefined {
    return this.data.lookId;
  }

  get upgradeLevels(): { [key: string]: number } {
    return { ...this.data.upgradeLevels };
  }

  getCharacterVisuals(): CharacterVisuals {
    // Try to get the current selected look from the LooksService first if possible
    if (Game.Core?.UserLooksService) {
      const selectedLook = Game.Core.UserLooksService.getSelectedLook();

      if (selectedLook) {
        return {
          spriteName: selectedLook.spriteKey,
          animationKey: selectedLook.animationKey,
          scale: DEFAULT_SCALE,
        };
      }
    }

    return {
      spriteName: "char1",
      animationKey: "char1_run",
      scale: DEFAULT_SCALE,
    };
  }

  getTotalUpgrades(): number {
    return Object.values(this.upgradeLevels).reduce(
      (sum, level) => sum + level,
      0
    );
  }

  isDefault(): boolean {
    return this.id === "default-guy";
  }

  getStatValue(stat: string): number {
    return this.stats[stat];
  }

  /**
   * Get the look upgrades for a specific stat
   */
  private getLookUpgradesForStat(stat: string): number {
    // Try to get the current selected look from the LooksService
    if (Game.Core?.UserLooksService) {
      const selectedLook = Game.Core.UserLooksService.getSelectedLook();
      if (selectedLook && selectedLook.characteristicsUpgrades) {
        // Return the stat upgrade value from the look if it exists
        return selectedLook.characteristicsUpgrades[stat] || 0;
      }
    }
    return 0;
  }

  getStatValueWithUpgrades(stat: string): number {
    // Get the current stat value directly from the upgradesInfo
    const upgradesInfo = Game.Core.UserCharactersService?.getUpgradesInfo();
    const currentStatValue =
      upgradesInfo?.currentStats?.[stat] ?? this.getStatValue(stat);

    // Add any look-specific upgrades
    const lookStatUpgrade = this.getLookUpgradesForStat(stat);

    return currentStatValue + lookStatUpgrade;
  }

  getAllStatsWithUpgrades(): CharacterStats {
    // Always use currentStats from upgradesInfo
    const upgradesInfo = Game.Core.UserCharactersService?.getUpgradesInfo();
    const baseStats = { ...this.stats, ...(upgradesInfo?.currentStats || {}) };

    // Get all look upgrades if available
    const lookUpgrades: CharacterStats = {};
    if (Game.Core?.UserLooksService) {
      const selectedLook = Game.Core.UserLooksService.getSelectedLook();
      if (selectedLook && selectedLook.characteristicsUpgrades) {
        // Apply all look upgrades to the stats
        Object.entries(selectedLook.characteristicsUpgrades).forEach(
          ([stat, value]) => {
            lookUpgrades[stat] = value;
          }
        );
      }
    }

    // Combine base stats with look upgrades
    const result: CharacterStats = { ...baseStats };

    // Add the lookUpgrades values to the corresponding stats in the result
    Object.entries(lookUpgrades).forEach(([stat, value]) => {
      if (result[stat] !== undefined) {
        result[stat] += value;
      } else {
        result[stat] = value;
      }
    });

    return result;
  }

  toJSON(): CharacterData {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      stats: this.stats,
      startingWeapon: this.startingWeapon.toString(),
      lookId: this.lookId,
      upgradeLevels: this.upgradeLevels,
    };
  }

  setUpgradeLevel(stat: string, level: number): void {
    this.data.upgradeLevels[stat] = level;
  }
}
